import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// components
import FormButton from "../../formComponents/FormButton";
import Loading from "../../components/Loading";
import DropDown from "../../formComponents/DropDown";
import InlineInput from "../../formComponents/InlineInput";

// styles and images
import Close from "../../assets/close.svg";
import "../companies/AddCompany.css";

// store
import { userStore, triggersStore, dbDataStore, messageAndErrorStore } from "../../store/Store";

export default function AddFuelSurcharge(props) {
  // global state
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setFuelSurchargeTrigger = triggersStore(
    (data) => data.setFuelSurchargeTrigger
  );
  const setFuelSurchargeData = dbDataStore((data) => data.setFuelSurchargeData);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)
  
  const methodOptions = [
    {value: 7, label: "Per ST"},
    {value: 8, label: "Flat"},
    {value: 40, label: "Per Mile"},
  ]

  // state
  let id = 0;
  if (props.id) {
    id = props.id;
  }
  const [oldName, setOldName] = useState("");
  const [formError, setFormError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [dataPending, setDataPending] = useState(false);
  const [file, setFile] = useState();
  
  const [fsc, setFSC] = useState({
    id: 0,
    owner_company_id: currentCompany.id,
    fsc_name: "",
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    method_id: 0,
  });
  const navigate = useNavigate();

  const { exists } = useVerifyUnique(
    props.docs,
    "fsc_name",
    fsc.fsc_name,
    oldName
  );
  
  useEscape(setFuelSurchargeTrigger, [props.setDocId], [""], isPending);

  useEffect(() => {
    if (props.id) {
      setDataPending(true);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/fsc-rate-sheet/${props.id}`,
        requestOptions
      )
        .then((res) => {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/login");
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (data.error) {
            setFormError("Could not retrieve rate sheet");
          } else {
            setFSC(data.fsc_rate_sheet);
            setOldName(data.fsc_rate_sheet.fsc_name);
            setDataPending(false);
          }
        })
        .catch((_) => {
          setFormError("Something went wrong");
          return;
        })
        .finally(() => setIsPending(false));
      return () => controller.abort();
    }
  }, [
    props.id,
    setOldName,
    id,
    JwtToken,
    setFormError,
    setAuthStatus,
    navigate,
    currentCompany,
  ]);

  const handleClose = () => {
    setFuelSurchargeTrigger(false);
    props.setDocId("");
    setFormError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFSC({
      ...fsc,
      [name]: value,
    });
  };

  const handleFile = (e) => {
    let f = e.target.files[0];
    setFormError(null);
    if (!f) {
      setFormError("Please Select a file");
      return;
    } else if (!f.type.includes("text/csv")) {
      setFormError("Please use the template FSC csv file");
      return;
    }
    setFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    
    setIsPending(true);
    setFormError(null);
    
    if (exists) {
      toggleNewMessage(
        "These FSC Rates already exists, please use a different name 😅"
      );
      setIsPending(false);
      return;
    }
    
    const formData = new FormData()
    if (file) (
      formData.append("file", file)
    )
    formData.append("method_id", fsc.method_id)
    formData.append("company_id", fsc.owner_company_id)
    formData.append("fsc_name", fsc.fsc_name)
    formData.append("updated_by_id", fsc.updated_by_id)
    
    let method = "POST";
    if (id > 0) {
      formData.append("id", id)
      method = "PATCH";
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: method,
      headers: headers,
      body: formData,
      signal: controller.signal,
    };

    fetch(`${process.env.REACT_APP_BACKEND}/pc/fsc-rate-sheet/0`, requestOptions)
    .then(res => {
      if (!res.ok) {
        if (res.status === 409) {
          toggleNewMessage("Incorrect Template for Method")
          return Promise.resolve()
        } else if (res.status === 401) {
          navigate("/login")
          return Promise.resolve()
        } else {
          toggleNewMessage(`Something went wrong ${res.status}`)
          return Promise.resolve()
        }
      } else {
        return res.json()
      }
    })
    .then(data => {
      if (!data) {
        return
      } else {
        setFuelSurchargeData(data)
        setFuelSurchargeTrigger(false)
        setIsPending(false)
        props.setDocId("")
      }
    })
    .catch(err => {console.log(err)})
    .finally(() => setIsPending(false))
    return () => controller.abort()
  };

  return !dataPending ? (
    <div className="popup">
      <div className="popup-inner-add-company">
        <h3>Add FSC Rate Sheet</h3>
        {!isPending && <img src={Close} onClick={handleClose} alt="close" />}
        <form onSubmit={handleSubmit}>
          <InlineInput
            title="Name"
            classname="col-sm-4 col-form-label fw-bold"
            inputClass="col-sm-7"
            type={"text"}
            name={"fsc_name"}
            value={fsc ? fsc.fsc_name : ""}
            onChange={handleChange}
            placeholder={"Enter FSC Name"}
            required={true}
          />
          <InlineInput
            title={"FSC File"}
            classname="col-sm-4 col-form-label fw-bold"
            inputClass="col-sm-7"
            type={"file"}
            name={"fsc_file"}
            onChange={handleFile}
            required={id === 0 ? true : false}
          />
          <DropDown 
            title="Hauling Method"
            name="method_id"
            options={methodOptions}
            onChange={(o) => setFSC({...fsc, method_id: o.value})}
            value={methodOptions.filter(x => x.value === fsc.method_id) || null}
            required={true}
          />
          <FormButton pending={isPending} name={"Submit"} />
        </form>
        {formError && <p className="error">{formError}</p>}
      </div>
    </div>
  ) : (
    <Loading />
  );
}
